'use client';

import { type ReactNode, useId } from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { ROW_ENTER_CLASS } from './motion';
import { SearchLink } from './SearchLink';

/** The arrow keys find the panel's rows by their `data-search-row`. */
export const SEARCH_ROW_SELECTOR = '[data-search-row]';

// ── Row ──

interface SearchRowProps {
  href: string;
  onSelect: () => void;
  children: ReactNode;
}

/**
 * One result, 40px on two lines. Its hover fill comes from the list around it:
 * the header panel slides one highlight under the pointer and the arrow keys.
 */
export function SearchRow({ href, onSelect, children }: SearchRowProps) {
  const id = useId();
  return (
    // The link is the option; the list item only holds it.
    <li role="none" data-search-row className={ROW_ENTER_CLASS}>
      <SearchLink
        id={id}
        role="option"
        href={href}
        onClick={onSelect}
        className="flex h-10 w-full items-center gap-2.5 rounded-[8px] px-2 outline-none"
      >
        {children}
      </SearchLink>
    </li>
  );
}

// ── Parts ──

interface RowTextProps {
  title: ReactNode;
  /** After the title, never truncated — the 🔥 of a trending pair. */
  badge?: ReactNode;
  meta?: ReactNode;
}

/** The row's two lines: its name, and what to know about it. */
export function RowText({ title, badge, meta }: RowTextProps) {
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
      <span className="flex min-w-0 items-center gap-1 text-[13px] font-medium leading-4 tracking-[-0.13px] text-v2-text-primary">
        <span className="truncate">{title}</span>
        {badge}
      </span>
      {meta ? (
        <span className="flex min-w-0 items-center gap-1 text-[11.5px] leading-[14px] text-v2-text-secondary">
          {meta}
        </span>
      ) : null}
    </span>
  );
}

interface RowFiguresProps {
  value: ReactNode;
  detail?: ReactNode;
  /** The day's change takes its direction's color. */
  detailClassName?: string;
}

/** The figures on the right: the number that matters, and what it means. */
export function RowFigures({ value, detail, detailClassName }: RowFiguresProps) {
  return (
    <span className="flex max-w-[130px] shrink-0 flex-col items-end gap-[3px] text-right tabular-nums">
      <span className="text-[13px] font-semibold leading-4 text-v2-text-primary">{value}</span>
      {detail ? (
        <span
          className={cn(
            'max-w-full truncate text-[11.5px] leading-[14px] text-v2-text-secondary',
            detailClassName,
          )}
        >
          {detail}
        </span>
      ) : null}
    </span>
  );
}
